import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { LessonMode } from '../../../Constants';
import { register } from '../../../i18n';
import OptionalImage from '../OptionalImage';
import RichTextEditor, { ADVANCED_CKEDITOR_TOOLBAR, BASIC_CKEDITOR_TOOLBAR, MATH_TOOLBAR, MATH_WITH_TABLE_TOOLBAR } from '../RichTextEditor';
import Guideline from '../tools/Guideline';
import responseService from '../../services/ResponseService';
import styleService from '../../services/StyleService';
import PlayerService from '../../services/PlayerService';
import UtilsService from '../../services/UtilsService';
import '../../../css/LongText.scss';
import HtmlComponent from '../HtmlComponent';
import useStyleEvents from '../../../hooks/useStyleEvents';
import PrintQuestionNumber from './PrintQuestionNumber';
import ShowPreviousResponseButton from '../ShowPreviousResponseButton';
import SubmissionWarningDialog from '../SubmissionWarningDialog';
import ShowPreviousResponseConfirmDialog from '../ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from '../PreviewUnavailableDialog';

const t = register('GlobalQuestionLabels');

const LongText = observer(({ lessonElementId }) => {
  const {
    lessonManager, // eslint-disable-line no-unused-vars
    responseManager,
    toolbarManager
  } = useContext(MobXProviderContext);

  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);

  const model = lessonManager.getSlideModel(lessonElementId);
  const textBody = model.questionText || '';

  let toolbar = null;
  if (model.toolbarType === 'basicToolbar') {
    toolbar = BASIC_CKEDITOR_TOOLBAR;
  } else if (model.toolbarType === 'mathToolbar') {
    toolbar = MATH_TOOLBAR;
  } else if (model.toolbarType === 'advancedToolbar') {
    toolbar = ADVANCED_CKEDITOR_TOOLBAR;
  } else if (model.toolbarType === 'mathWithTableToolbar') {
    toolbar = MATH_WITH_TABLE_TOOLBAR;
  }

  const lessonElementState = responseManager.getLessonElementState(lessonElementId);

  if (!lessonElementState) {
    return (<div className='free-response-question' />);
  }

  const readOnly = lessonElementState.readOnly || lessonManager.isReadOnly;

  const { currentResponseAnswer } = lessonElementState;

  // Check if this question has response dependency
  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;
 
  if (responseDependencyValue) {
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId && 
                                responseDependencyValue.questionForeverId &&
                                responseDependencyValue.lessonId !== '' &&
                                responseDependencyValue.questionForeverId !== '';
      }
    } else {
      // Handle old format where value is a string (questionId)
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }
  const isSubmitted = lessonElementState.isSubmitted;

  // Handler for showing previous response
  const handleShowPreviousResponse = async () => {
    if (lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      // Check if this is a differentLesson source
      if (isDifferentLessonSource) {
        // Check if we're in activity mode
        if (PlayerService.isActivityMode()) {
          // Call the API to fetch prior responses
          await lessonElementState.questionBehavior.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        } else {
          // Show dialog for non-activity mode
          setShowPreviewUnavailable(true);
        }
        return;
      }
      
      // Check if user has current content that would be overwritten
      if (lessonElementState.questionBehavior.hasCurrentContent(lessonElementId)) {
        setShowPreviousResponseConfirm(true);
      } else {
        // No current content, proceed directly
        lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
      }
    }
  };

  const handleConfirmShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
    if (lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
    }
  };

  const handleCancelShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
  };

  // Handler for submission with warning
  const handleSubmitWithWarning = () => {
    if (hasResponseDependency && !isSubmitted) {
      setShowSubmissionWarning(true);
    } else {
      // Proceed with normal submission
      responseService.submitResponse(lessonElementId);
    }
  };

  const handleConfirmSubmission = () => {
    setShowSubmissionWarning(false);
    responseService.submitResponse(lessonElementId);
  };

  const handleCancelSubmission = () => {
    setShowSubmissionWarning(false);
  };

  const maxLength = parseInt(model.maxLength) || null;
  const maxWords = parseInt(model.maxWordCount) || null;

  const normalizedResponseText = UtilsService.stripTagsAndEntities(currentResponseAnswer.text || '').trim();
  const normalizedDefaultText = UtilsService.stripTagsAndEntities(model.defaultText || '').trim();
  const isPlaceholderResponse = !normalizedResponseText
    || (normalizedDefaultText && normalizedResponseText === normalizedDefaultText)
    || normalizedResponseText === t('enterHere');
  const printResponseHtml = isPlaceholderResponse ? '' : currentResponseAnswer.text;

  useStyleEvents(lessonElementId);

  return (
    <div className='free-response-question'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div className='  test-item-question'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={textBody} />
      </div>
      <OptionalImage model={model} runtime={true} />
      
      {/* Show Previous Response Button */}
      {hasResponseDependency && lessonManager.playerMode !== LessonMode.SCORING && lessonManager.playerMode !== LessonMode.REVIEW && (
        <div className='response-dependency-controls'>
          <ShowPreviousResponseButton
            lessonElementId={lessonElementId}
            onShowPreviousResponse={handleShowPreviousResponse}
            isDisabled={isSubmitted}
          />
        </div>
      )}
      
      <div className='test-item-answers'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && (
          <div className='print-line-wrapper'>
            {(lessonManager.isForceTeacher) ? (
              <HtmlComponent htmlStr={model.validation.solution} />
            ) : (
              <>
                <div className='print-line'><HtmlComponent htmlStr={printResponseHtml} /></div>
                <div className='print-line' />
                <div className='print-line' />
              </>
            )}
          </div>
        )}

        {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && (
          <>
            {(model.offlineGrading) ? <></> : (
              <RichTextEditor
                data={currentResponseAnswer.text}
                disabled={readOnly}
                lessonElementId={lessonElementId}
                maxCharacters={maxLength}
                maxWords={maxWords}
                onBlur={() => styleService.resetStyleVar('--ck-z-modal')}
                onChange={(data) => responseService.textChangeHandler(data, lessonElementId)}
                onFocus={() => styleService.setStyleVar('--ck-z-modal', '102')}
                placeHolder={lessonManager.playerMode === LessonMode.PRINT_PREVIEW ? '' : t('enterHere')}
                toolbar={toolbar}
              />
            )}
          </>
        )}

      </div>
      
      {/* Submission Warning Dialog */}
      <SubmissionWarningDialog
        isOpen={showSubmissionWarning}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelSubmission}
        message={t('submissionWarningMessage')}
      />
      
      {/* Show Previous Response Confirmation Dialog */}
      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={handleConfirmShowPreviousResponse}
        onCancel={handleCancelShowPreviousResponse}
        message={t('showPreviousResponseWarning')}
      />
      
      {/* Preview Unavailable Dialog */}
      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message="This feature is only available during live question answering sessions. It cannot be previewed in this mode."
      />
    </div>
  );
});

export default LongText;
